import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { useSelector } from "react-redux";
import vmTheme from "../constants/theme";
import Toggle from "./Toggle";
import Typography from "./Typography";
import { font } from "../constants/font";
import CustomTooltipComponent from "./CustomTooltip";
import Restriction from "./Restriction";
import content from "../constants/content";
import { STATIC_PATH } from "../constants/config";
import { PLAN } from "../constants";
import useUpgrade from "../helper/hooks/useUpgrade";
import { UploadBar, UploadStriped } from "./MusicTile";

const Wrapper = styled.div`
  width: 316px;
  height: auto;
  background: #ffffff;
  box-shadow: 0px 0.5px 1px ${(props) => props.theme.rgbaBlack};
  border: ${(props) =>
    props.isChecked
      ? `1px solid ${props.theme.secondaryBorderColor}`
      : `1px solid ${props.theme.iconsBorder}`};
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 12px;
  position: relative;
  cursor: ${(props) => (props.isLoading ? "progress" : "pointer")};
  transition: height 0.1s;
  ${(props) =>
    props.isDisabled &&
    css`
      cursor: not-allowed;
    `}
  ${(props) =>
    props.isExpand &&
    css`
      height: auto;
    `}
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: start;
  gap: 12px;
  align-items: center;
  .bgRestriction {
    justify-content: start;
    label {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 1px 6px;
    }
  }
  .bg-toggle {
    margin-left: auto;
  }
  + div .restriction-btn {
    padding: 3px 8px !important;
  }
  ${(props) =>
    props.isProgress &&
    css`
      justify-content: space-between;
      width: ${({ isShowClose }) => (isShowClose ? "246px" : "280px")};
      @media (max-width: 768px) {
        width: 100%;
      }
    `}
`;

const Icon = styled.img`
  width: ${(props) => props.width || "32px"};
  height: ${(props) => props.height || "32px"};
  border-radius: 8px;
`;

const UpgradeSection = styled.div`
  height: 49px;
  border-radius: ${(props) => (props.isChecked ? "8px" : "0px")};
  background: ${(props) =>
    props.isChecked
      ? props.theme.expandableUpgradActivebg
      : props.theme.expandableUpgradbg};
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin: ${(props) => (props.isChecked ? "0px 16px 16px" : "auto")};
  position: relative;
  opacity: 1;
  overflow: hidden;
  transition:
    height 0.1s ease,
    opacity 0.1s ease;

  &::before {
    content: url(${(props) => props.isChecked ? `${STATIC_PATH}vm-info.svg` : "unset"});
    position: absolute;
    left: 8px;
  }
  .premiunContent {
    padding: ${(props) => (props.isChecked ? "0px 12px 0px 32px" : "0px")};
  }

  ${({ isDisclaimer }) => isDisclaimer &&
    css`
    width: 308px;
    border-radius: 8px;
    margin-bottom: 4px;
    background: ${(props) => props.theme.disclaimerBg} ;
    padding-left: 20px !important;
    &::before {
    content: url(${STATIC_PATH}disclaimer.svg);
    position: absolute;
    top: 8px;
    left: 8px;
    height: 16px;
    width: 16px;
  }
    `}
`;

const Container = styled.div`
  height: ${(props) => (props.hasSubHeading ? "70px" : "56px")};
  padding: 12px 16px;
  border-top: 1px solid #e3e4e8;
  .progress-bar {
    width: ${({ isShowClose }) => isShowClose ? "244px" : "280px"};
    margin-top: 8px;
    @media (max-width: 768px) {
      width: 100%;
    }
  }
  .close-icon {
    display: ${({ isShowClose }) => isShowClose ? "block" : "none"};
    position: absolute;
    height: 24px;
    width: 24px;
    top: calc(50% - 12px);
    right: 16px;
    cursor: pointer;
  }
  ${(props) =>
    props.isDisabled &&
    css`
      opacity: 0.5;
    `}
`;

const ExpandableButton = ({
  isExpandable,
  heading,
  subHeading,
  checked,
  handleCheckBox,
  icon,
  iconHeight,
  iconWidth,
  children,
  isDisabled,
  tooltipMessage,
  isUpgrade,
  isLimitExceed,
  info,
  disclaimer,
  modalContent,
  progress,
  onClose,
  isLoading
}) => {
  const { showUpgrade } = useUpgrade();
  const theme = useSelector((state) => state.app.get("theme"));
  const { plan } = useSelector((store) => store.userDetails);
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  const clickHandler = (e) => {
    e && e.preventDefault() && e.stopPropagation();
    if (!isDisabled) {
      if (isUpgrade || isLimitExceed) {
        setIsChecked(false);
        showUpgrade(modalContent);
      } else {
        handleCheckBox(!isChecked);
      }
    }
  };

  const Tooltip = (isDisabled && !disclaimer) ? CustomTooltipComponent : React.Fragment;

  return (
    <Tooltip
      tooltipId={`expand-tooltip-${heading}`}
      dataTooltip={tooltipMessage}
      tooltipPosition={"bottom"}
    >
      <Wrapper
        isExpand={isChecked && isExpandable}
        plan={plan === PLAN.LITE}
        isChecked={isChecked}
        isDisabled={isDisabled}
        isLoading={isLoading}
        data-tooltip-id={`expand-tooltip-${heading}`}
      >
        <Container isDisabled={isDisabled} hasSubHeading={subHeading} isShowClose={!!onClose}>
          <Flex
            isProgress={progress}
            isShowClose={!!onClose}
            onClick={!progress ? clickHandler : undefined}
          >
            {icon && <Icon src={icon} height={iconHeight} width={iconWidth} />}
            <div>
              <Typography
                content={heading}
                color={vmTheme[theme].tabTextColor}
                display={subHeading ? "block" : "content"}
                font={font.boldBase}
                cursor={isDisabled ? "not-allowed" : "pointer"}
              />
              {subHeading ? (
                <Typography
                  content={subHeading}
                  color={vmTheme[theme].labelSubtextColor}
                  font={font.normalMicro_11}
                  cursor={"pointer"}
                />
              ) : null}
              {isUpgrade || isLimitExceed ? (
                <Restriction
                  className="bgRestriction"
                  content={isLimitExceed ? "Limit Exceeded" : "Upgrade"}
                  type={isLimitExceed ? "LIMIT_EXCEED" : "UPGRADE"}
                  top={
                    isLimitExceed
                      ? "0px"
                      : subHeading
                        ? "4px"
                        : "calc(50% - 11px)"
                  }
                  right={isLimitExceed ? "0px" : subHeading ? "4px" : "8px"}
                  position={isLimitExceed ? "relative" : "absolute"}
                />
              ) : null}
            </div>
            {progress && (
              <Typography
                content={`${progress}%`}
                color={vmTheme[theme].tabTextColor}
                display={"content"}
                font={font.boldBase}
                cursor={isLoading ? "progress" : "pointer"}
              />
            )}
            {!isUpgrade && !progress ? (
              <Toggle
                style={{ marginLeft: "auto" }}
                toggleColor={vmTheme[theme].checkBoxTickColor}
                value={isChecked}
                clickHandler={clickHandler}
                isDisabled={isDisabled}
                isLoading={isLoading}
              />
            ) : null}
          </Flex>
          {progress && (
            <div className="progress-bar">
              <UploadStriped>
                <UploadBar width={progress} />
              </UploadStriped>
              <Icon
                className="close-icon"
                src={`${STATIC_PATH}close-icon.svg`}
                onClick={onClose}
              />
            </div>
          )}
        </Container>
        {isLimitExceed || (isChecked && info) ? (
          <UpgradeSection isChecked={isChecked}>
            <Typography
              content={isChecked && info ? info : content.WHOOPS}
              className="premiunContent"
              color={vmTheme[theme].panelPrimaryColor}
              font={font.mediumMini_12}
              enableTrim={false}
              width={isChecked ? "auto" : isLimitExceed ? "160px" : null}
            />
            {isChecked ? null : (
              <Restriction
                content={"Upgrade"}
                type="UPGRADE"
                backgroundColor={vmTheme[theme].primaryColor}
                onClick={() => showUpgrade(modalContent)}
              />
            )}
          </UpgradeSection>
        ) : null}
        {disclaimer ? (
          <UpgradeSection isDisclaimer={disclaimer} isChecked={isChecked}>
            <Typography
              content={disclaimer}
              className={"disclaimer"}
              color={vmTheme[theme].panelPrimaryColor}
              font={font.normalMicroLarge_16}
              enableTrim={false}
              width={"264px"}
              margin={"0px 10px"}
              cursor={isDisabled ? "not-allowed" : "pointer"}
            />
          </UpgradeSection>
        ) : null}

        {isExpandable && children}
      </Wrapper>
    </Tooltip>
  );
};
ExpandableButton.propTypes = {
  isExpandable: PropTypes.bool,
  heading: PropTypes.string,
  subHeading: PropTypes.string,
  checked: PropTypes.bool,
  handleCheckBox: PropTypes.func,
  icon: PropTypes.string,
  iconHeight: PropTypes.string,
  iconWidth: PropTypes.string,
  children: PropTypes.element,
  isDisabled: PropTypes.bool,
  tooltipMessage: PropTypes.string,
  isUpgrade: PropTypes.bool,
  isLimitExceed: PropTypes.bool,
  info: PropTypes.string,
  disclaimer: PropTypes.string,
  modalContent: PropTypes.object,
  progress: PropTypes.number,
  onClose: PropTypes.func,
  isLoading: PropTypes.bool
};

export default ExpandableButton;
